import React from 'react';
import { Spin, Avatar, List, Tag } from 'antd';
import {
    TeamOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    UserOutlined,
    DollarOutlined,
    SafetyCertificateOutlined,
    CheckOutlined,
} from '@ant-design/icons';
import { useScheduleDetail } from '../hooks/useSchedules';
import StatusBadge from '@/components/ui/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Container } from '@/components/ui/Container';
import { DayChip } from '../style/schedule.style';

const ALL_DAYS = [
    { key: 'mon', label: 'Mon' },
    { key: 'tue', label: 'Tue' },
    { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' },
    { key: 'fri', label: 'Fri' },
    { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' },
];

export const ScheduleDetailPanel = ({ scheduleId, onClose }) => {
    const { data: schedule, isLoading, isError } = useScheduleDetail(scheduleId);

    if (!scheduleId) return null;
    if (isLoading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
                <Spin size="large" />
            </div>
        );
    }
    if (isError || !schedule) {
        return (
            <Container $padding="24px" style={{ textAlign: 'center', color: '#ef4444' }}>
                Failed to load schedule details.
            </Container>
        );
    }

    const workingDaysList = Array.isArray(schedule.workingDays)
        ? schedule.workingDays.map((d) => String(d).toLowerCase())
        : [];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, paddingTop: 4 }}>
            {/* Header Title & Status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--ant-color-border-secondary, #f1f5f9)', paddingBottom: 16 }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--ant-color-text, #1e293b)' }}>
                        {schedule.title}
                    </h3>
                    <span style={{ fontSize: 12, color: 'var(--ant-color-text-secondary, #64748b)' }}>
                        Shift Schedule Details
                    </span>
                </div>
                <StatusBadge status={schedule.status} />
            </div>

            {/* Key Metrics Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                <div style={{ background: 'var(--ant-color-fill-alter, #f8fafc)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--ant-color-border, #e2e8f0)' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <ClockCircleOutlined style={{ color: '#6366f1' }} /> Time Range
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        {schedule.startTime} - {schedule.endTime}
                    </div>
                </div>

                <div style={{ background: 'var(--ant-color-fill-alter, #f8fafc)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--ant-color-border, #e2e8f0)' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <SafetyCertificateOutlined style={{ color: '#10b981' }} /> Grace Time
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        {schedule.graceTime ?? 0} mins
                    </div>
                </div>

                <div style={{ background: 'var(--ant-color-fill-alter, #f8fafc)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--ant-color-border, #e2e8f0)' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <CalendarOutlined style={{ color: '#f59e0b' }} /> Calendar End
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        Day {schedule.calendarEndDay ?? 1} of month
                    </div>
                </div>

                <div style={{ background: 'var(--ant-color-fill-alter, #f8fafc)', borderRadius: 12, padding: '12px 14px', border: '1px solid var(--ant-color-border, #e2e8f0)' }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <TeamOutlined style={{ color: '#8b5cf6' }} /> Members
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                        {schedule.users?.length ?? schedule.userCount ?? 0} Assigned
                    </div>
                </div>
            </div>

            {/* Working Days */}
            <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CalendarOutlined /> Working Days Schedule
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ALL_DAYS.map((day) => {
                        const isWorking = workingDaysList.includes(day.key);
                        return (
                            <DayChip
                                key={day.key}
                                $isSelected={isWorking}
                                style={{ pointerEvents: 'none', padding: '5px 12px', fontSize: 11 }}
                            >
                                {isWorking && <CheckOutlined style={{ fontSize: 10 }} />}
                                <span>{day.label}</span>
                            </DayChip>
                        );
                    })}
                </div>
            </div>

            {/* Policies Card */}
            <div style={{ background: 'var(--ant-color-fill-alter, #f8fafc)', borderRadius: 14, padding: 16, border: '1px solid var(--ant-color-border, #e2e8f0)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <DollarOutlined style={{ color: '#10b981' }} /> Salary & Deduction Configuration
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, borderBottom: '1px dashed #e2e8f0', paddingBottom: 8 }}>
                    <span style={{ color: '#64748b' }}>Salary Policy:</span>
                    <Tag color={schedule.salaryType === 'sandwich' ? 'purple' : 'blue'} style={{ fontWeight: 600, borderRadius: 10, margin: 0 }}>
                        {schedule.salaryType === 'sandwich' ? 'Policy 2: Sandwich Rule' : 'Policy 1: Fixed Monthly'}
                    </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                    <span style={{ color: '#64748b' }}>Absent Deduction:</span>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>
                        {schedule.absentDeductionType === 'fixed_amount'
                            ? `Fixed Rs. ${parseFloat(schedule.fixedAbsentDeductionAmount || 0).toLocaleString()} / day`
                            : 'Per Day Working Rate'}
                    </span>
                </div>
            </div>

            {/* Assigned Employees List */}
            <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <TeamOutlined style={{ color: '#6366f1' }} /> Assigned Employees ({schedule.users?.length ?? 0})
                </div>

                {schedule.users && schedule.users.length > 0 ? (
                    <div style={{ maxHeight: 180, overflowY: 'auto', borderRadius: 12, border: '1px solid var(--ant-color-border, #e2e8f0)', padding: '4px 12px', background: '#fff' }}>
                        <List
                            itemLayout="horizontal"
                            dataSource={schedule.users}
                            renderItem={(user) => (
                                <List.Item style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                    <List.Item.Meta
                                        avatar={
                                            <Avatar src={user.avatarUrl} icon={<UserOutlined />} style={{ background: '#6366f1' }} />
                                        }
                                        title={
                                            <span style={{ fontWeight: 600, color: '#1e293b', fontSize: 13 }}>
                                                {user.name || '—'}
                                            </span>
                                        }
                                        description={<span style={{ fontSize: 11, color: '#64748b' }}>{user.email}</span>}
                                    />
                                </List.Item>
                            )}
                        />
                    </div>
                ) : (
                    <div style={{ padding: '16px', background: 'var(--ant-color-fill-alter, #f8fafc)', borderRadius: 12, border: '1px dashed var(--ant-color-border, #cbd5e1)', textAlign: 'center', color: '#94a3b8', fontSize: 12, fontStyle: 'italic' }}>
                        No employees currently assigned to this shift schedule.
                    </div>
                )}
            </div>

            {/* Footer Action */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--ant-color-border-secondary, #f1f5f9)' }}>
                <Button $primary={false} onClick={onClose} $padding="8px 24px" $size="13px">
                    Close
                </Button>
            </div>
        </div>
    );
};

export default ScheduleDetailPanel;
